import { useMutation } from "@tanstack/react-query";
import { updateWorkoutItem } from "api-client";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Checkbox, Label } from "ui";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutItemMutationScope } from "src/workouts/useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

export const EachSideCheckbox = observer(function EachSideCheckbox(props: {
  workoutItemId: string;
}) {
  const translation = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const workoutItemsPermissions = useWorkoutPermissions();
  const workoutItem = workoutEditStore.getWorkoutItemById(props.workoutItemId);
  const scope = useWorkoutItemMutationScope(props.workoutItemId);
  const { withSaving } = useAuthenticatedNavigationContext();

  const mutation = useMutation({
    scope,
    mutationFn: async (eachSide: boolean) => {
      return await withSaving(
        updateWorkoutItem({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: props.workoutItemId,
          },
          body: { eachSide },
        }),
      );
    },
    onMutate: (eachSide) => {
      workoutEditStore.updateWorkoutItem(props.workoutItemId, { eachSide });
    },
  });

  if (
    !(workoutItem.type === "NORMAL" || workoutItem.type === "ALTERNATE") ||
    !workoutItemsPermissions.canEditItems
  ) {
    return null;
  }

  const eachSideChecked = workoutItem.eachSide ?? false;

  return (
    <div className="flex items-center gap-1.5">
      <Checkbox
        id={`each-side-${props.workoutItemId}`}
        className="size-4"
        checked={eachSideChecked}
        onCheckedChange={(checked) => {
          mutation.mutate(checked === true);
        }}
      />
      <Label
        htmlFor={`each-side-${props.workoutItemId}`}
        className="text-sm font-medium"
      >
        {translation.t("workoutItemSets.eachSide", {
          defaultValue: "Each side",
        })}
      </Label>
    </div>
  );
});
